import { NavBar } from '@hfzq/crab-react';
import { NavBarProps } from '@hfzq/crab-react/esm/components/nav-bar';
import React, { CSSProperties, FC } from 'react';
import classNames from 'classnames';
import { crabDevice } from '@hfzq/crab-utils';
import { isNoneValue } from '@src/helper/common/none-value';
import { goback } from '@src/routes';
import { usePreloadImages } from '@src/helper/hooks/use-preload-images';
import { BottomInset, TopInset } from './content-inset';
import { StickyInBody } from '../sticky-in-body';
import styles from './index.module.less';

interface AppBodyProps {
  /**
   * 返回文案
   */
  back?: NavBarProps['back'];
  /**
   * 返回图标
   */
  backArrow?: NavBarProps['backArrow'];
  /**
   * 状态栏左侧控件
   */
  left?: NavBarProps['left'];
  /**
   * 状态栏右侧控件
   */
  right?: NavBarProps['right'];
  /**
   * 返回按钮点击事件
   */
  onBack?: NavBarProps['onBack'];
  /**
   * 设置状态栏标题
   */
  title?: NavBarProps['children'];
  /**
   * 设置顶部状态栏(不含信号栏底部部分)样式
   */
  navBarStyle?: NavBarProps['style'];
  /**
   * 设置顶部状态栏(含信号栏底部部分)className
   */
  className?: string;
  /**
   * 设置顶部状态栏(含信号栏底部部分)样式
   */
  style?: CSSProperties;
  /**
   * 页面内容
   */
  children?: React.ReactNode;
  /**
   * 是否根据设备垫起底部
   */
  addBottomInset?: boolean;
  /**
   * 设置背景容器className
   */
  appBgCls?: string;
  /**
   * 设置没有占位背景容器className
   */
  appTransBgCls?: string;
  /**
   * 设置header背景容器className
   */
  appHeadBgCls?: string;
  /**
   * 设置背景元素
   */
  appBgEle?: React.ReactNode;
  /**
   * 设置没有占位背景元素
   */
  appTransBgEle?: React.ReactNode;
  /**
   * 设置背景元素样式
   */
  appHeadBgStyle?: CSSProperties;
  /**
   * 图片加载地址
   * */
  preloadImageUrls?: string[];
  loadIndicatorBackgroundColor?: string;
  /**
   * 设置背景容器zIndex
   */
  zIndex?: number;
}

export const AppBody: FC<AppBodyProps> = ({
  back,
  backArrow = <BackArrow />,
  left,
  right,
  onBack,
  title,
  className,
  style,
  children,
  navBarStyle,
  addBottomInset = true,
  appBgCls,
  appTransBgCls,
  appHeadBgCls,
  appBgEle,
  appTransBgEle,
  appHeadBgStyle,
  preloadImageUrls = [],
  loadIndicatorBackgroundColor = 'transparent',
  zIndex = 1001,
}) => {
  const defaultBackgroundColorRef = React.useRef<string>();
  const imagesLoaded = usePreloadImages({
    imageUrls: preloadImageUrls,
    usePreload: preloadImageUrls.length > 0,
  }); // 根据状态决定是否使用图片预加载
  // eslint-disable-next-line no-console
  console.log('imagesLoaded', imagesLoaded);
  const handleBackArrowClick = (e: Event) => {
    e?.stopPropagation();
    if (!onBack) {
      goback();
      return;
    }
    onBack();
  };
  const showGoback = () => {
    if (crabDevice.isTZT) {
      return true;
    }
    const { state } = window.history;
    if (!state) {
      return false;
    }
    if (!isNoneValue(state.idx) && state.idx < 1) {
      return false;
    }
    return true;
  };

  React.useEffect(() => {
    if (!preloadImageUrls.length) {
      return;
    }
    if (!defaultBackgroundColorRef.current) {
      defaultBackgroundColorRef.current = document.body.style.backgroundColor;
    }
    if (imagesLoaded) {
      // 加载完成后修改整个页面的背景颜色
      document.body.style.backgroundColor = loadIndicatorBackgroundColor;
    } else {
      // 加载中恢复页面的初始背景颜色
      document.body.style.backgroundColor = defaultBackgroundColorRef.current;
    }
  }, [imagesLoaded, loadIndicatorBackgroundColor, preloadImageUrls]);

  return (
    <>
      {imagesLoaded ? (
        <>
          <StickyInBody zIndex={zIndex} topContentClassName="nav-bar-container">
            <div className={classNames(styles.navContainer, className)} style={style}>
              <TopInset />
              <NavBar
                back={back}
                backArrow={!showGoback() ? null : backArrow}
                left={left}
                right={right}
                onBack={handleBackArrowClick as () => void}
                // transform 修复ios上渲染出现黑线的问题
                style={{ backgroundColor: 'transparent', ...navBarStyle, transform: 'translate3d(0,0,0)' }}
              >
                {title}
              </NavBar>
              {(!!appHeadBgCls || !!appBgEle) && (
                <div style={appHeadBgStyle} className={classNames(styles.appHeadBackground, appHeadBgCls)}>
                  {appBgEle}
                </div>
              )}
            </div>
          </StickyInBody>
          {children}
          {addBottomInset && <BottomInset />}
          {(!!appBgCls || !!appBgEle) && <div className={classNames(styles.appBackground, appBgCls)}>{appBgEle}</div>}
          {(!!appTransBgCls || !!appTransBgEle) && (
            <div className={classNames(styles.appBackground, appTransBgCls)}>{appTransBgEle}</div>
          )}
        </>
      ) : (
        <div className={styles.loadIndicatorContainer}>
          <LoadIndicator />
        </div>
      )}
    </>
  );
};

interface BackArrowProps {
  clickBackReport?: () => void;
}
export const BackArrow = ({ clickBackReport }: BackArrowProps) => (
  <div
    className={styles.backArrow}
    onClick={() => {
      clickBackReport?.();
    }}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="24" viewBox="0 0 12 24" fill="none">
      <path
        d="M8.42031 19.4703C8.70858 19.761 9.17947 19.7578 9.46375 19.4632C9.73878 19.1783 9.73641 18.726 9.45842 18.4439L3.09593 11.9884L9.49564 5.55597C9.77583 5.27434 9.77927 4.82033 9.50338 4.53449C9.21996 4.24085 8.75082 4.2367 8.46227 4.52529L1.03868 11.9497C1.01729 11.9711 1.01722 12.0058 1.03852 12.0273L8.42031 19.4703Z"
        fill="white"
      />
    </svg>
  </div>
);

export const BlackBackArrow = () => (
  <div className={styles.backArrow}>
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="24" viewBox="0 0 12 24" fill="none">
      <path
        d="M8.42031 19.4703C8.70858 19.761 9.17947 19.7578 9.46375 19.4632C9.73878 19.1783 9.73641 18.726 9.45842 18.4439L3.09593 11.9884L9.49564 5.55597C9.77583 5.27434 9.77927 4.82033 9.50338 4.53449C9.21996 4.24085 8.75082 4.2367 8.46227 4.52529L1.03868 11.9497C1.01729 11.9711 1.01722 12.0058 1.03852 12.0273L8.42031 19.4703Z"
        fill="#222222"
      />
    </svg>
  </div>
);

export const LoadIndicator = () => (
  <div className={styles.loadIndicator}>
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g>
        <g id="Frame" clipPath="url(#clip0_4515_17109)">
          <path
            id="Vector"
            d="M16.8402 6.61314C16.8402 6.88061 16.9465 7.13711 17.1356 7.32624C17.3247 7.51536 17.5812 7.62161 17.8487 7.62161C18.1161 7.62161 18.3726 7.51536 18.5618 7.32624C18.7509 7.13711 18.8571 6.88061 18.8571 6.61314C18.8571 6.34568 18.7509 6.08917 18.5618 5.90005C18.3726 5.71092 18.1161 5.60468 17.8487 5.60468C17.5812 5.60468 17.3247 5.71092 17.1356 5.90005C16.9465 6.08917 16.8402 6.34568 16.8402 6.61314Z"
            fill="#CDCDCD"
          />
          <path
            id="Vector_2"
            d="M18.7134 12.0631C18.7134 12.2266 18.7455 12.3884 18.8081 12.5395C18.8706 12.6905 18.9623 12.8278 19.0779 12.9434C19.1935 13.059 19.3307 13.1507 19.4818 13.2132C19.6328 13.2758 19.7947 13.308 19.9582 13.308C20.1216 13.308 20.2835 13.2758 20.4345 13.2132C20.5856 13.1507 20.7228 13.059 20.8384 12.9434C20.954 12.8278 21.0457 12.6905 21.1082 12.5395C21.1708 12.3884 21.203 12.2266 21.2029 12.0631C21.203 11.8996 21.1708 11.7377 21.1082 11.5867C21.0457 11.4356 20.954 11.2984 20.8384 11.1828C20.7228 11.0672 20.5856 10.9755 20.4345 10.9129C20.2835 10.8503 20.1216 10.8181 19.9582 10.8181C19.7947 10.8181 19.6328 10.8503 19.4818 10.9129C19.3307 10.9755 19.1935 11.0672 19.0779 11.1828C18.9623 11.2984 18.8706 11.4356 18.8081 11.5867C18.7455 11.7377 18.7134 11.8996 18.7134 12.0631Z"
            fill="#CDCDCD"
          />
          <path
            id="Vector_3"
            d="M16.4305 17.5403C16.4305 17.929 16.5849 18.3017 16.8597 18.5765C17.1345 18.8513 17.5073 19.0057 17.8959 19.0057C18.2845 19.0057 18.6572 18.8513 18.9321 18.5765C19.2069 18.3017 19.3612 17.929 19.3612 17.5403C19.3612 17.1517 19.2069 16.779 18.9321 16.5042C18.6572 16.2294 18.2845 16.075 17.8959 16.075C17.5073 16.075 17.1345 16.2294 16.8597 16.5042C16.5849 16.779 16.4305 17.1517 16.4305 17.5403Z"
            fill="#CDCDCD"
          />
          <path
            id="Vector_4"
            d="M10.7261 19.7306C10.7261 19.9536 10.7699 20.1743 10.8552 20.3803C10.9405 20.5863 11.0656 20.7734 11.2232 20.9311C11.3808 21.0887 11.568 21.2138 11.7739 21.2991C11.9799 21.3844 12.2006 21.4283 12.4236 21.4283C12.6465 21.4283 12.8673 21.3844 13.0732 21.2991C13.2792 21.2138 13.4663 21.0887 13.624 20.9311C13.7816 20.7734 13.9066 20.5863 13.9919 20.3803C14.0772 20.1743 14.1211 19.9536 14.1211 19.7306C14.1211 19.5077 14.0772 19.287 13.9919 19.081C13.9066 18.875 13.7816 18.6879 13.624 18.5302C13.4663 18.3726 13.2792 18.2475 13.0732 18.1622C12.8673 18.0769 12.6465 18.033 12.4236 18.033C12.2006 18.033 11.9799 18.0769 11.7739 18.1622C11.568 18.2475 11.3808 18.3726 11.2232 18.5302C11.0656 18.6879 10.9405 18.875 10.8552 19.081C10.7699 19.287 10.7261 19.5077 10.7261 19.7306Z"
            fill="#CDCDCD"
          />
          <path
            id="Vector_5"
            d="M5.13245 17.4542C5.13245 17.7025 5.18135 17.9484 5.27637 18.1778C5.37139 18.4072 5.51067 18.6156 5.68624 18.7912C5.86181 18.9668 6.07025 19.1061 6.29965 19.2011C6.52905 19.2961 6.77491 19.345 7.02321 19.345C7.27151 19.345 7.51738 19.2961 7.74678 19.2011C7.97618 19.1061 8.18461 18.9668 8.36019 18.7912C8.53576 18.6156 8.67503 18.4072 8.77005 18.1778C8.86507 17.9484 8.91398 17.7025 8.91398 17.4542C8.91398 16.9528 8.71478 16.4719 8.36019 16.1173C8.0056 15.7627 7.52468 15.5635 7.02321 15.5635C6.52175 15.5635 6.04083 15.7627 5.68624 16.1173C5.33165 16.4719 5.13245 16.9528 5.13245 17.4542Z"
            fill="#CDCDCD"
          />
          <path
            id="Vector_6"
            d="M10.4557 4.50151C10.4557 5.03226 10.6665 5.54127 11.0418 5.91657C11.4171 6.29187 11.9261 6.50271 12.4569 6.50271C12.9876 6.50271 13.4967 6.29187 13.872 5.91657C14.2473 5.54127 14.4581 5.03226 14.4581 4.50151C14.4581 4.23871 14.4063 3.97848 14.3058 3.73568C14.2052 3.49288 14.0578 3.27227 13.872 3.08644C13.6861 2.90062 13.4655 2.75321 13.2227 2.65264C12.9799 2.55207 12.7197 2.50031 12.4569 2.50031C12.1941 2.50031 11.9339 2.55207 11.6911 2.65264C11.4483 2.75321 11.2277 2.90062 11.0418 3.08644C10.856 3.27227 10.7086 3.49288 10.608 3.73568C10.5075 3.97848 10.4557 4.23871 10.4557 4.50151Z"
            fill="#CDCDCD"
          />
          <path
            id="Vector_7"
            d="M5.02209 6.66035C5.02209 6.92315 5.07386 7.18338 5.17443 7.42618C5.275 7.66898 5.4224 7.88959 5.60823 8.07542C5.79406 8.26125 6.01467 8.40865 6.25747 8.50922C6.50027 8.60979 6.7605 8.66155 7.0233 8.66155C7.2861 8.66155 7.54633 8.60979 7.78912 8.50922C8.03192 8.40865 8.25253 8.26125 8.43836 8.07542C8.62419 7.88959 8.7716 7.66898 8.87217 7.42618C8.97274 7.18338 9.0245 6.92315 9.0245 6.66035C9.0245 6.1296 8.81366 5.62059 8.43836 5.24529C8.06306 4.86999 7.55405 4.65915 7.0233 4.65915C6.49255 4.65915 5.98353 4.86999 5.60823 5.24529C5.23294 5.62059 5.02209 6.1296 5.02209 6.66035Z"
            fill="#CDCDCD"
          />
          <path
            id="Vector_8"
            d="M2.99475 12.0002C2.99475 12.2506 3.04407 12.4985 3.1399 12.7299C3.23572 12.9612 3.37618 13.1714 3.55324 13.3485C3.7303 13.5255 3.94051 13.666 4.17185 13.7618C4.40319 13.8576 4.65114 13.9069 4.90155 13.9069C5.15195 13.9069 5.39991 13.8576 5.63125 13.7618C5.86259 13.666 6.0728 13.5255 6.24986 13.3485C6.42692 13.1714 6.56738 12.9612 6.6632 12.7299C6.75903 12.4985 6.80835 12.2506 6.80835 12.0002C6.80835 11.4944 6.60745 11.0094 6.24986 10.6518C5.89227 10.2942 5.40726 10.0934 4.90155 10.0934C4.39583 10.0934 3.91083 10.2942 3.55324 10.6518C3.19565 11.0094 2.99475 11.4944 2.99475 12.0002Z"
            fill="#CDCDCD"
          />
        </g>
      </g>
      <defs>
        <clipPath id="clip0_4515_17109">
          <rect width="19" height="19" fill="white" transform="translate(2.5 2.5)" />
        </clipPath>
      </defs>
    </svg>
  </div>
);
